import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";
import { API_BASE_URL } from "@/lib/api-base";

export interface UserProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  role: "admin" | "member" | null;
  is_owner: boolean;
  company_id: string | null;
  company_name: string | null;
  // Structured address fields — replaces old flat `address` string
  company_street: string | null;
  company_postal_code: string | null;
  company_city: string | null;
  company_country: string | null;
  company_address_extra: string | null;
  billing_same_as_company: boolean;
  billing_street: string | null;
  billing_postal_code: string | null;
  billing_city: string | null;
  billing_country: string | null;
  billing_address_extra: string | null;
  billing_contact_name: string | null;
  billing_email: string | null;
  vat_id: string | null;
  /** Original flat address string — kept for reference during migration */
  address_legacy: string | null;
  needs_address_review: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  needsOnboarding: boolean;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = API_BASE_URL;

async function fetchProfile(accessToken: string): Promise<UserProfile | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      id: data.user_id,
      first_name: data.first_name,
      last_name: data.last_name,
      phone: data.phone,
      role: data.role,
      is_owner: data.is_owner ?? false,
      company_id: data.company?.id ?? null,
      company_name: data.company?.name ?? null,
      // Structured address fields from the company object
      company_street: data.company?.company_street ?? null,
      company_postal_code: data.company?.company_postal_code ?? null,
      company_city: data.company?.company_city ?? null,
      company_country: data.company?.company_country ?? null,
      company_address_extra: data.company?.company_address_extra ?? null,
      billing_same_as_company: data.company?.billing_same_as_company ?? false,
      billing_street: data.company?.billing_street ?? null,
      billing_postal_code: data.company?.billing_postal_code ?? null,
      billing_city: data.company?.billing_city ?? null,
      billing_country: data.company?.billing_country ?? null,
      billing_address_extra: data.company?.billing_address_extra ?? null,
      billing_contact_name: data.company?.billing_contact_name ?? null,
      billing_email: data.company?.billing_email ?? null,
      vat_id: data.company?.vat_id ?? null,
      address_legacy: data.company?.address_legacy ?? null,
      needs_address_review: data.company?.needs_address_review ?? false,
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshProfile = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) {
      const p = await fetchProfile(data.session.access_token);
      setProfile(p);
    }
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      if (s?.access_token) {
        const p = await fetchProfile(s.access_token);
        setProfile(p);
      }
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, s) => {
      setSession(s);
      if (s?.access_token) {
        const p = await fetchProfile(s.access_token);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const logout = useCallback(async () => {
    // Call backend logout to revoke refresh token
    if (session?.access_token) {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
      } catch {
        // Best-effort — still sign out locally
      }
    }
    await supabase.auth.signOut();
    setProfile(null);
  }, [session?.access_token]);

  const isAuthenticated = !!session?.user;
  const needsOnboarding =
    isAuthenticated && profile !== null && !profile.company_id;

  return (
    <AuthContext.Provider
      value={{
        user: session?.user ?? null,
        session,
        profile,
        isLoading,
        isAuthenticated,
        needsOnboarding,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
